// Globals
import { ChangeEvent, ComponentProps, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import dayjs from 'dayjs';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getOrdersList, ordersSelector } from '../../feature';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';
import { selectAdminProfile } from '@/modules/AdminProfile';

// Models
import { Roles } from '@/models/admins.model';

// Components
import { StyledFilterRow } from './OrdersFiltersContainerStyles';
import { Input, Select } from '@/ui';
import { RangePicker } from '@/components';

// Helpers
import {
  OrdersFiltersFields,
  getDeliveryTypeOptions,
  getStatusOptions,
  getPaymentStatusFilterOptions,
  getPaymentChannelOptions,
  getSortingOptions,
  getUtmSourceOptions,
  getPartnerConfirmationFilterOptions,
} from '../../helpers';
import { GetOrdersListDTO } from '../../dtos';
import { DateFormats } from '@/constants/dates';
import { formatDateForApiRequest } from '@/utils';

interface IOrdersFiltersContainerContainer {
  children?: ReactNode;
  className?: string;
}

const OrdersFiltersContainer = ({ children, className }: IOrdersFiltersContainerContainer) => {
  const { t } = useTranslation(['orders']);
  const dispatch = useAppDispatch();
  const { query, managers } = useAppState(ordersSelector);
  const profile = useAppState(selectAdminProfile);
  const { canManage } = usePermissions();
  // Filtering orders by manager is part of regular order processing (order.manage).
  const canManageOrderManager = canManage(Resource.ORDER);
  const isLogistician = profile?.role === Roles.LOGISTICIAN;

  const managersOptions = managers.map((m) => ({ label: m.full_name, value: m.id }));

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetOrdersListDTO(searchParams);
        dispatch(getOrdersList(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined, page: 1 };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const onSelectChange = (value: unknown, key: OrdersFiltersFields) => {
    const newQuery = { ...query, [key]: value || undefined, page: 1 };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateRangeChange = (_: unknown, dateStrings: [string, string]) => {
    const newQuery = {
      ...query,
      [OrdersFiltersFields.DATE_FROM]:
        formatDateForApiRequest(dateStrings[0], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      [OrdersFiltersFields.DATE_TO]:
        formatDateForApiRequest(dateStrings[1], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      page: 1,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handlePaymentDateRangeChange = (_: unknown, dateStrings: [string, string]) => {
    const newQuery = {
      ...query,
      [OrdersFiltersFields.PAYMENT_DATE_FROM]:
        formatDateForApiRequest(dateStrings[0], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      [OrdersFiltersFields.PAYMENT_DATE_TO]:
        formatDateForApiRequest(dateStrings[1], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      page: 1,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  // antd ships its own nested dayjs copy, so its RangePicker value type is nominally different from the
  // top-level dayjs. Cast through the component's own prop type to bridge the two dayjs instances.
  type RangeValue = ComponentProps<typeof RangePicker>['value'];

  const toRangeValue = (from?: string, to?: string): RangeValue =>
    (from || to ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null) as unknown as RangeValue;

  const createdRange = toRangeValue(query?.[OrdersFiltersFields.DATE_FROM], query?.[OrdersFiltersFields.DATE_TO]);
  const paymentRange = toRangeValue(
    query?.[OrdersFiltersFields.PAYMENT_DATE_FROM],
    query?.[OrdersFiltersFields.PAYMENT_DATE_TO],
  );

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Input
            placeholder={t('orders_search_placeholder')}
            name={OrdersFiltersFields.NUMBER}
            value={query?.[OrdersFiltersFields.NUMBER] ?? ''}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        {!isLogistician && (
          <th>
            <Select
              options={getPartnerConfirmationFilterOptions(t)}
              placeholder={t('orders_partner_confirmation_select_placeholder')}
              value={query?.[OrdersFiltersFields.PARTNER_CONFIRMATION_STATUS] || undefined}
              onChange={(v) => onSelectChange(v, OrdersFiltersFields.PARTNER_CONFIRMATION_STATUS)}
              size="large"
              allowClear
            />
          </th>
        )}
        {canManageOrderManager && (
          <th>
            <Select
              options={managersOptions}
              placeholder={t('orders_manager_select_placeholder')}
              value={query?.[OrdersFiltersFields.MANAGER_ID] || undefined}
              onChange={(v) => onSelectChange(v, OrdersFiltersFields.MANAGER_ID)}
              size="large"
              allowClear
            />
          </th>
        )}
        <th>
          <Select
            options={getStatusOptions(t)}
            placeholder={t('orders_status_select_placeholder')}
            value={query?.[OrdersFiltersFields.STATUS] || undefined}
            onChange={(v) => onSelectChange(v, OrdersFiltersFields.STATUS)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('orders_search_placeholder')}
            name={OrdersFiltersFields.CONTACT_NAME}
            value={query?.[OrdersFiltersFields.CONTACT_NAME] || ''}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('orders_search_placeholder')}
            name={OrdersFiltersFields.CONTACT_PHONE}
            value={query?.[OrdersFiltersFields.CONTACT_PHONE] || ''}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <RangePicker
            placeholder={[t('orders_range_picker_placeholder_from'), t('orders_range_picker_placeholder_to')]}
            value={createdRange}
            onChange={handleDateRangeChange}
            size="middle"
            format={DateFormats.DATE_WITHOUT_HOURS}
          />
        </th>
        <th>
          <Select
            options={getSortingOptions(t)}
            placeholder={t('orders_delivery_sorting_select_placeholder')}
            value={query?.[OrdersFiltersFields.SORT_DIRECTION] || undefined}
            onChange={(v) => onSelectChange(v, OrdersFiltersFields.SORT_DIRECTION)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getPaymentChannelOptions(t)}
            placeholder={t('orders_status_select_placeholder')}
            value={query?.[OrdersFiltersFields.PAYMENT_CHANNEL] || undefined}
            onChange={(v) => onSelectChange(v, OrdersFiltersFields.PAYMENT_CHANNEL)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getPaymentStatusFilterOptions(t)}
            placeholder={t('orders_status_select_placeholder')}
            value={query?.[OrdersFiltersFields.PAYMENT_STATUS] || undefined}
            onChange={(v) => onSelectChange(v, OrdersFiltersFields.PAYMENT_STATUS)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <RangePicker
            placeholder={[t('orders_range_picker_placeholder_from'), t('orders_range_picker_placeholder_to')]}
            value={paymentRange}
            onChange={handlePaymentDateRangeChange}
            size="middle"
            format={DateFormats.DATE_WITHOUT_HOURS}
          />
        </th>
        <th>
          <Select
            options={getDeliveryTypeOptions(t)}
            placeholder={t('orders_delivery_type_select_placeholder')}
            value={query?.[OrdersFiltersFields.DELIVERY_TYPE] || undefined}
            onChange={(v) => onSelectChange(v, OrdersFiltersFields.DELIVERY_TYPE)}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Select
            options={getUtmSourceOptions(t)}
            placeholder={t('orders_utm_source_filter_placeholder')}
            value={query?.[OrdersFiltersFields.UTM_SOURCE] || undefined}
            onChange={(v) => onSelectChange(v, OrdersFiltersFields.UTM_SOURCE)}
            size="large"
            allowClear
          />
        </th>
      </StyledFilterRow>
    </thead>
  );
};

export default OrdersFiltersContainer;
